import { coarsePointer, prefersReducedMotion } from './env'
import { clamp, damp } from './math'

export interface GyroOrientation {
  roll: number   // normalized [-1, 1] (left / right)
  pitch: number  // normalized [-1, 1] (tilt forward / back)
  rawGamma: number
  rawBeta: number
}

export type GyroCallback = (orientation: GyroOrientation) => void

export class GyroParallaxManager {
  private active = false
  private listeners: GyroCallback[] = []
  private targetRoll = 0
  private targetPitch = 0
  private currentRoll = 0
  private currentPitch = 0
  private rafId = 0
  private lastTime = 0
  private boundOrientationHandler: ((e: DeviceOrientationEvent) => void) | null = null
  private boundPermissionGesture: (() => void) | null = null

  constructor() {
    this.init()
  }

  private init(): void {
    if (typeof window === 'undefined') return
    if (prefersReducedMotion()) return

    // Verify device supports orientation events
    if (!('DeviceOrientationEvent' in window)) return

    // If fine pointer and not touch, desktop mice handle pointermove
    if (!coarsePointer && !('ontouchstart' in window)) {
      return
    }

    // Check iOS 13+ permission requirement
    const doe = window.DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>
    }

    if (typeof doe.requestPermission === 'function') {
      // iOS requires explicit permission requested from a user gesture
      this.boundPermissionGesture = () => {
        if (this.active) return
        doe
          .requestPermission!()
          .then((permissionState) => {
            if (permissionState === 'granted') {
              this.startListening()
            }
          })
          .catch(() => {})
          .finally(() => {
            this.removePermissionGestureListeners()
          })
      }
      this.addPermissionGestureListeners()
    } else {
      // Android / standard devices: start listening directly
      this.startListening()
    }
  }

  private addPermissionGestureListeners(): void {
    if (!this.boundPermissionGesture) return
    window.addEventListener('pointerdown', this.boundPermissionGesture, { passive: true, once: true })
    window.addEventListener('touchstart', this.boundPermissionGesture, { passive: true, once: true })
  }

  private removePermissionGestureListeners(): void {
    if (!this.boundPermissionGesture) return
    window.removeEventListener('pointerdown', this.boundPermissionGesture)
    window.removeEventListener('touchstart', this.boundPermissionGesture)
    this.boundPermissionGesture = null
  }

  private startListening(): void {
    if (this.active) return
    this.active = true

    this.boundOrientationHandler = (e: DeviceOrientationEvent) => {
      this.handleDeviceOrientation(e)
    }

    window.addEventListener('deviceorientation', this.boundOrientationHandler, { passive: true })
    this.lastTime = performance.now()
    this.startLoop()
  }

  private handleDeviceOrientation(e: DeviceOrientationEvent): void {
    if (e.beta === null || e.gamma === null) return

    let beta = e.beta // [-180, 180]
    let gamma = e.gamma // [-90, 90]

    // Screen orientation calibration (portrait vs landscape)
    const orientationAngle =
      (window.screen?.orientation && window.screen.orientation.angle) ||
      (typeof window.orientation === 'number' ? window.orientation : 0)

    if (orientationAngle === 90) {
      const temp = beta
      beta = -gamma
      gamma = temp
    } else if (orientationAngle === -90 || orientationAngle === 270) {
      const temp = beta
      beta = gamma
      gamma = -temp
    } else if (orientationAngle === 180) {
      beta = -beta
      gamma = -gamma
    }

    // Natural reading posture holds phone at ~45° incline from vertical
    const neutralBeta = 45
    // ±28 degrees gives responsive [-1, 1] range
    this.targetPitch = clamp((beta - neutralBeta) / 28, -1, 1)
    this.targetRoll = clamp(gamma / 26, -1, 1)
  }

  private startLoop(): void {
    const loop = (now: number) => {
      if (!this.active) return
      this.rafId = requestAnimationFrame(loop)
      const dt = Math.min(0.05, Math.max(0.001, (now - this.lastTime) / 1000))
      this.lastTime = now

      // Smooth damping to eliminate sensor noise & jitter
      this.currentRoll = damp(this.currentRoll, this.targetRoll, 4.5, dt)
      this.currentPitch = damp(this.currentPitch, this.targetPitch, 4.5, dt)

      const payload: GyroOrientation = {
        roll: this.currentRoll,
        pitch: this.currentPitch,
        rawGamma: this.targetRoll * 26,
        rawBeta: this.targetPitch * 28 + 45,
      }

      for (const cb of this.listeners) {
        cb(payload)
      }
    }

    this.rafId = requestAnimationFrame(loop)
  }

  subscribe(callback: GyroCallback): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback)
    }
  }

  dispose(): void {
    this.active = false
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
    if (this.boundOrientationHandler) {
      window.removeEventListener('deviceorientation', this.boundOrientationHandler)
      this.boundOrientationHandler = null
    }
    this.removePermissionGestureListeners()
    this.listeners = []
  }
}
